// @ts-nocheck
/*
 * Port of views/dashboard/campaigns/summary.js (920 lines).
 * BYTE-IDENTICAL — Completed-campaigns summary. Same swaps as CampaignsListPage:
 * PrimeReact DataTable/Column → obd3-gui DataTable + tanstack ColumnDef,
 * PrimeReact Button → shadcn Button. Take-Action Dropdown kept as react-bootstrap.
 * Purple/indigo summary chrome (gradient banner + skpi-* cards) preserved verbatim.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { useNavigate } from 'react-router-dom';
import { MODULE_ACL, ROUTES, USER_ROLES } from '@/utilities/constant';
import { exportToCSV, formatErrorDesc, hasPermission } from '@/utilities/helpers';
import { getUser, Storage } from '@/utilities/setting';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_VOICE_CAMPAIGNS_SUMMARY,
  API_VOICE_CAMPAIGNS_UPDATE,
  DOWNLOAD_REPORT_CAMPAIGN,
} from '@/service/url';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import {
  FiBarChart2,
  FiFlag,
  FiHash,
  FiCheckCircle,
  FiTrendingUp,
  FiEye,
  FiRefreshCw,
} from 'react-icons/fi';
import NumbersViewerModal from './NumbersViewerModal';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { canDownloadReports } from '@/utilities/promptUploadAccess';
import {
  Download,
  RefreshCw,
  Eye,
  Copy,
  BarChart3,
  FileText as FileIcon,
  Link2,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const getTodayStr = () => new Date().toLocaleDateString('en-CA');
const getFirstOfMonthStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-CA');
};
const getSixMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toLocaleDateString('en-CA');
};

const CampaignsSummary = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loggedUserRole, setLoggedUserRole] = useState('');
  const [loggedUserId, setLoggedUserId] = useState<any>(null);
  const [fromDate, setFromDate] = useState(getFirstOfMonthStr());
  const [toDate, setToDate] = useState(getTodayStr());
  const [appliedDates, setAppliedDates] = useState({
    start: getFirstOfMonthStr(),
    end: getTodayStr(),
  });
  const [searchValue, setSearchValue] = useState('');
  const [numbersViewer, setNumbersViewer] = useState({ show: false, id: null, name: null, total: 0 } as any);

  const buildDate = (date, isEnd = false) => {
    if (!date) return null;
    const [y, m, d] = date.split('-');
    return new Date(
      y,
      m - 1,
      d,
      isEnd ? 23 : 0,
      isEnd ? 59 : 0,
      isEnd ? 59 : 0,
      isEnd ? 999 : 0,
    ).getTime();
  };

  const buildQuery = (role, agentId, start, end) => {
    const queryParams = new URLSearchParams();
    if (role === USER_ROLES.CALL_CENTRE_AGENT && agentId) queryParams.append('agent_id', agentId);
    if (start) queryParams.append('start_date', start);
    if (end) queryParams.append('end_date', end);
    return queryParams.toString();
  };

  const fetchCampaigns = async (
    role = loggedUserRole,
    agentId = loggedUserId,
    start = fromDate,
    end = toDate,
  ) => {
    setLoading(true);
    const finalUrl = `${API_VOICE_CAMPAIGNS_SUMMARY}?${buildQuery(role, agentId, start, end)}`;
    try {
      const res = await getApiCall(finalUrl, {});
      onCampaignListSuccess(res);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const finalUrl = `${API_VOICE_CAMPAIGNS_SUMMARY}?${buildQuery(
      loggedUserRole,
      loggedUserId,
      appliedDates.start,
      appliedDates.end,
    )}`;
    try {
      const res = await getApiCall(finalUrl, {});
      onCampaignListSuccess(res);
    } catch (error) {
      onError(error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchUserDataAndCampaigns = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User data not found in Storage');
        return;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in Storage user:', e);
        return;
      }
      const role = user?.role;
      setLoggedUserRole(role);
      if (role === USER_ROLES.CALL_CENTRE_AGENT) {
        const id = user?.agentData?.id;
        setLoggedUserId(id);
        fetchCampaigns(role, id);
      } else {
        fetchCampaigns(role);
      }
    };
    fetchUserDataAndCampaigns();
  }, []);

  function onCampaignListSuccess(resp) {
    setCampaigns(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const formatDateTime = (dateString, timeString) => {
    if (!dateString || !timeString) {
      return { full: 'Invalid Date', compact: 'Invalid Date' };
    }
    const date = new Date(`${dateString}T${timeString}`);
    if (isNaN(date.getTime())) {
      return { full: 'Invalid Date', compact: 'Invalid Date' };
    }
    const formattedFull = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return { full: formattedFull, compact: `${date.toISOString().slice(0, 10)} - ${timeString}` };
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'campaigns-summary',
        data: filteredCampaigns,
        columns: [
          { header: 'Campaign', accessor: (row) => row.campaign_name || '-' },
          {
            header: 'Created On',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          { header: 'Username', accessor: (row) => row.user?.username || '-' },
          { header: 'Start Date', accessor: (row) => formatDateTime(row.start_date, row.start_hours).full || '-' },
          { header: 'End Date', accessor: (row) => formatDateTime(row.end_date, row.end_hours).full || '-' },
          {
            header: 'Locations',
            accessor: (row) =>
              row.locations?.length ? row.locations.map((loc) => loc.location_name).join(', ') : '-',
          },
          { header: 'Total Numbers', accessor: (row) => row.total_nos || 0 },
          { header: 'Processed', accessor: (row) => row.nos_processed || 0 },
          {
            header: 'Completion %',
            accessor: (row) =>
              row.total_nos
                ? `${Math.round((Number(row.nos_processed || 0) / Number(row.total_nos)) * 100)}%`
                : '0%',
          },
          { header: 'Error Description', accessor: (row) => formatErrorDesc(row.error_desc) },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  // Summary API already restricts to Completed(2) server-side.
  // Name search is now handled by the DataTable's built-in globalFilter,
  // so this memo only owns the date-range filter.
  const filteredCampaigns = campaigns.filter((campaign) => {
    const start = buildDate(appliedDates.start);
    const end = buildDate(appliedDates.end, true);
    const createdAtTs = campaign?.created_at ? new Date(campaign.created_at).getTime() : null;
    if (start && createdAtTs && createdAtTs < start) return false;
    if (end && createdAtTs && createdAtTs > end) return false;
    return true;
  });

  const updateStatus = (id, campaign_status) => {
    setLoading(true);
    const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
    postApiCall(url, { campaign_status })
      .then(() => {
        toast.success('Campaign Status Updated');
        handleRefresh();
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  const handleGenerateReport = (rowData) => {
    let userId: any = null;
    try {
      userId = JSON.parse(Storage.getItem('user'))?.id;
    } catch (e) {
      userId = null;
    }
    postApiCall(DOWNLOAD_REPORT_CAMPAIGN, {
      campaign_id: rowData.campaign_id,
      campaign_name: rowData.campaign_name,
      user_id: userId,
    })
      .then(() => {
        toast.success('Report generation in-progress. Check Download Reports.');
        navigate(`/${ROUTES.IVR.DOWNLOAD_REPORT}`);
      })
      .catch(onError);
  };

  const handleGenerateWebhookReport = (rowData) => {
    let userId: any = null;
    try {
      userId = JSON.parse(Storage.getItem('user'))?.id;
    } catch (e) {
      userId = null;
    }
    postApiCall(DOWNLOAD_REPORT_CAMPAIGN, {
      campaign_id: rowData.campaign_id,
      campaign_name: rowData.campaign_name,
      user_id: userId,
      report_type: 'campaign_webhook',
    })
      .then(() => {
        toast.success('Webhook report generation in-progress. Check Download Reports.');
        navigate(`/${ROUTES.IVR.DOWNLOAD_REPORT}`);
      })
      .catch(onError);
  };

  const handleAction = (action, id) => {
    if (action === 'view') {
      navigate(`/${ROUTES.CAMPAIGNS.DETAIL.replace(':id', id)}`);
    }
    if (action === 'pause') updateStatus(id, 8);
    if (action === 'resume') updateStatus(id, 10);
    if (action === 'stop') updateStatus(id, 6);
    if (action === 'cancel') updateStatus(id, 7);
    if (action === 'complete') updateStatus(id, 2);
    if (action === 'delete') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { is_deleted: 1 })
        .then(onDeleteSuccess)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'duplicate') {
      navigate(`/${ROUTES.CAMPAIGNS.DUPLICATE.replace(':duplicate_camp_id', id)}`);
    }
  };

  async function onDeleteSuccess() {
    toast.success('Campaign Deleted successfully');
    handleRefresh();
  }

  const STATUS_MAP = {
    0: 'Ready',
    1: 'Running',
    2: 'Complete',
    3: 'Low Balance',
    4: 'Error',
    5: 'Error',
    6: 'Cancelling',
    7: 'Cancelled',
    8: 'Pausing',
    9: 'Paused',
    10: 'Resuming',
    21: 'DND Processing',
    22: 'Waiting for Channel',
  };
  const TYPE_MAP = { 1: 'Click2Call', 2: 'Auto Dialer', 3: 'Predictive Dialer', 4: 'Power Dialer' };
  const TEMPLATE_MAP = { 0: 'Simple IVR', 1: 'DTMF', 2: 'Call Patch', 3: 'Custom IVR' };

  const actionBodyTemplate = (rowData) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm">Take Action</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => handleAction('view', rowData.campaign_id)}>
            <Eye className="h-4 w-4" /> View
          </DropdownMenuItem>
          {canAdd && (
            <DropdownMenuItem onClick={() => handleAction('duplicate', rowData.campaign_id)}>
              <Copy className="h-4 w-4" /> Clone Campaign
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() =>
              navigate(`/${ROUTES.CAMPAIGNS.REPORT.replace(':id', rowData.campaign_id)}`)
            }
          >
            <BarChart3 className="h-4 w-4" /> Detailed Report
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleGenerateReport(rowData)}>
            <FileIcon className="h-4 w-4" /> Generate Full Report
          </DropdownMenuItem>
          {Number(rowData?.webhook_count) > 0 && (
            <DropdownMenuItem onClick={() => handleGenerateWebhookReport(rowData)}>
              <Link2 className="h-4 w-4" /> Generate Webhook Report
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const stats = useMemo(() => {
    let total = 0;
    let processed = 0;
    filteredCampaigns.forEach((c) => {
      total += Number(c.total_nos || 0);
      processed += Number(c.nos_processed || 0);
    });
    const completionRate = total > 0 ? Math.round((processed / total) * 100) : 0;
    return { campaigns: filteredCampaigns.length, total, processed, completionRate };
  }, [filteredCampaigns]);

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CAMPAIGNS, 'read');
  const canAdd =
    isCallCentreAdmin && hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CAMPAIGNS, 'add');

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  // Don't blank the page on refetch — only show full-page loader when
  // we have nothing to display yet.
  if (loading && campaigns.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  // Tone-tinted status pill matching Live Campaigns / other list pages.
  const statusToneMap: Record<number, string> = {
    0:  'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300',
    1:  'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
    2:  'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300',
    3:  'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
    4:  'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
    5:  'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
    6:  'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300',
    7:  'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-950/40 dark:text-pink-300',
    8:  'bg-cyan-100 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300',
    9:  'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300',
    10: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
    21: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300',
    22: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
  };
  const renderStatusPill = (status: any) => {
    const label = STATUS_MAP[status] ?? String(status ?? '—');
    const tone = statusToneMap[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300';
    return (
      <span className={'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' + tone}>
        {label}
      </span>
    );
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'campaign_id',
      header: 'ID',
      size: 80,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          #{row.original.campaign_id}
        </span>
      ),
    },
    {
      accessorKey: 'campaign_name',
      header: 'Campaign Name',
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left font-medium text-primary underline-offset-2 hover:underline"
          onClick={() =>
            navigate(`/${ROUTES.CAMPAIGNS.DETAIL.replace(':id', row.original.campaign_id)}`)
          }
        >
          {row.original.campaign_name}
        </button>
      ),
    },
    {
      accessorKey: 'campaign_status',
      header: 'Status',
      size: 130,
      cell: ({ row }) => renderStatusPill(row.original.campaign_status),
    },
    {
      id: 'error_desc',
      accessorFn: (row) => formatErrorDesc(row.error_desc),
      header: 'Error Description',
      cell: ({ getValue }) => (
        <span className="block max-w-[260px] truncate text-xs" title={getValue()}>{getValue()}</span>
      ),
    },
    {
      accessorKey: 'campaign_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {TYPE_MAP[row.original.campaign_type] || TEMPLATE_MAP[row.original.template_id] || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'start_date',
      header: 'Start Time',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.start_date, row.original.start_hours).full}
        </span>
      ),
    },
    {
      accessorKey: 'end_date',
      header: 'End Time',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.end_date, row.original.end_hours).full}
        </span>
      ),
    },
    {
      accessorKey: 'pause_time',
      header: 'Pause Time',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {row.original.pause_time || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'total_nos',
      header: 'Total Numbers',
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2 tabular-nums">
          <span className="font-semibold text-foreground">{row.original.total_nos ?? 0}</span>
          <button
            type="button"
            title="View numbers"
            onClick={() =>
              setNumbersViewer({
                show: true,
                id: row.original.campaign_id,
                name: row.original.campaign_name,
                total: Number(row.original.total_nos) || 0,
              })
            }
            className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </span>
      ),
    },
    {
      id: 'tcd',
      header: 'TCD',
      cell: ({ row }) =>
        Number(row.original.nos_processed || 0) + Number(row.original.auto_retry_count || 0),
    },
    {
      accessorKey: 'nos_processed',
      header: 'Calls Dialed',
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
          {row.original.nos_processed ?? 0}
        </span>
      ),
    },
    {
      id: 'pending_calls',
      header: 'Pending Calls',
      cell: ({ row }) =>
        Number(row.original.total_nos || 0) -
        Number(row.original.dnd_count || 0) -
        Number(row.original.nos_processed || 0),
    },
    { accessorKey: 'connected_calls', header: 'Connected Calls', cell: ({ row }) => row.original.connected_calls ?? 0 },
    // { accessorKey: 'pulses', header: 'Total Pulses', cell: ({ row }) => row.original.pulses ?? 0 },
    { accessorKey: 'dnd_count', header: 'DnD Count', cell: ({ row }) => row.original.dnd_count ?? 0 },
    // { accessorKey: 'dtmf_count', header: 'Dtmf Count', cell: ({ row }) => row.original.dtmf_count ?? 0 },
    // { accessorKey: 'dtmf1_count', header: 'Dtmf1 Count', cell: ({ row }) => row.original.dtmf1_count ?? 0 },
    // { accessorKey: 'dtmf2_count', header: 'Dtmf2 Count', cell: ({ row }) => row.original.dtmf2_count ?? 0 },
    { accessorKey: 'webhook_failed_count', header: 'Webhook Failed Count', cell: ({ row }) => row.original.webhook_failed_count ?? 0 },
    { accessorKey: 'sms_count', header: 'SMS Count', cell: ({ row }) => row.original.sms_count ?? 0 },
    { accessorKey: 'max_retries', header: 'Retry Count', cell: ({ row }) => row.original.max_retries ?? 0 },
    // { accessorKey: 'total_variable_count', header: 'Total Variable Count', cell: ({ row }) => row.original.total_variable_count ?? 0 },
    { accessorKey: 'auto_retry_count', header: 'No. Of retries', cell: ({ row }) => row.original.auto_retry_count ?? 0 },
    { id: 'actions', header: 'Action', enableSorting: false, cell: ({ row }) => actionBodyTemplate(row.original) },
    { id: 'retry', header: 'Retry', cell: ({ row }) => row.original.max_retries ?? 0 },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Campaigns Summary"
        description={`Completed campaigns overview · ${stats.campaigns} campaign${
          stats.campaigns === 1 ? '' : 's'
        }.`}
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            {canDownloadReports() && campaigns.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </>
        }
      />
      <div className="campaigns-container summary-scope">
      <div className="row">
        <div className="col-12">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">

              {/* Compact stat strip — one horizontal row of chips.
                  Icon well + label + value all inline, no wasted
                  vertical space. Completion Rate keeps its progress
                  bar as a thin underline. */}
              <div className="mb-3 mt-2 flex flex-wrap gap-2">
                <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-primary">
                    <FiFlag />
                  </span>
                  <span className="text-muted-foreground">Campaigns</span>
                  <span className="font-semibold text-foreground tabular-nums">{stats.campaigns}</span>
                </div>
                <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                    <FiHash />
                  </span>
                  <span className="text-muted-foreground">Total numbers</span>
                  <span className="font-semibold text-foreground tabular-nums">{stats.total}</span>
                </div>
                <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                    <FiCheckCircle />
                  </span>
                  <span className="text-muted-foreground">Processed</span>
                  <span className="font-semibold text-emerald-700 tabular-nums dark:text-emerald-400">
                    {stats.processed}
                  </span>
                </div>
                <div className="flex min-w-[180px] items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-primary">
                    <FiTrendingUp />
                  </span>
                  <span className="text-muted-foreground">Completion</span>
                  <span className="font-semibold text-primary tabular-nums">
                    {stats.completionRate}%
                  </span>
                  <span
                    className="ml-1 h-1 flex-1 overflow-hidden rounded-full bg-muted"
                    title={`${stats.completionRate}%`}
                  >
                    <span
                      className="block h-full bg-primary transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, stats.completionRate))}%` }}
                    />
                  </span>
                </div>
              </div>

              {/* ── Date filters ──────────────────────────────── */}
              <div className="mb-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    From
                  </label>
                  <CalendarDateInput
                    value={fromDate}
                    onChange={(e) => setFromDate(e)}
                    min={getSixMonthsAgoStr()}
                    max={getTodayStr()}
                    className="form-control"
                    style={{ height: '40px', width: '170px' }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    To
                  </label>
                  <CalendarDateInput
                    value={toDate}
                    onChange={(e) => setToDate(e)}
                    min={fromDate}
                    max={getTodayStr()}
                    className="form-control"
                    style={{ height: '40px', width: '170px' }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      fetchCampaigns(loggedUserRole, loggedUserId, fromDate, toDate);
                      setAppliedDates({ start: fromDate, end: toDate });
                    }}
                  >
                    Filter
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const start = getFirstOfMonthStr();
                      const end = getTodayStr();
                      setFromDate(start);
                      setToDate(end);
                      setAppliedDates({ start, end });
                      fetchCampaigns(loggedUserRole, loggedUserId, start, end);
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* ── Table ─────────────────────────────────────── */}
              <div className="pb-4">
                <div style={{ overflowX: 'auto' }} className="broad-scrollbar">
                  <DataTable
                    tableId="campaigns.summary"
                    columns={columns}
                    data={filteredCampaigns}
                    pageSize={10}
                    searchPlaceholder="Search by campaign name…"
                    emptyTitle="No completed campaigns"
                    disableExport
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <NumbersViewerModal
        show={numbersViewer.show}
        onHide={() => setNumbersViewer({ show: false, id: null, name: null, total: 0 })}
        campaignId={numbersViewer.id}
        campaignName={numbersViewer.name}
        expectedTotal={numbersViewer.total}
      />
      <style>{`
  .broad-scrollbar::-webkit-scrollbar { height: 8px; }
  .summary-banner {
    padding: 20px 22px; border-radius: 14px;
    background: linear-gradient(120deg, #4338ca 0%, #6d28d9 100%);
    box-shadow: 0 6px 18px rgba(79,70,229,0.25);
  }
  .summary-banner-icon {
    width: 48px; height: 48px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; color: #fff;
    background: rgba(255,255,255,0.18);
  }
  .summary-refresh-btn { color: #fff !important; }
  .summary-export-btn {
    background: rgba(255,255,255,0.15) !important;
    border-color: rgba(255,255,255,0.35) !important;
    color: #fff !important;
  }
  .skpi-card {
    border-radius: 14px; background: #fff;
    box-shadow: 0 2px 10px rgba(16,24,40,0.06);
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .skpi-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(16,24,40,0.10); }
  .skpi-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex: 0 0 auto; }
  .skpi-icon--sm { width: 30px; height: 30px; font-size: 15px; }
  .skpi-icon--indigo { background: #eae8ff; color: #4338ca; }
  .skpi-icon--blue   { background: #e8ecff; color: #3b47d6; }
  .skpi-icon--green  { background: #e3f6f0; color: #11707d; }
  .skpi-icon--purple { background: #f1e9ff; color: #6d28d9; }
  .skpi-label { font-size: 12px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
  .skpi-value { font-size: 26px; font-weight: 700; line-height: 1.1; }
  .summary-filter-btn { background: #6d28d9 !important; border-color: #6d28d9 !important; }
  .summary-refresh-btn .pi-spin { animation: pispin 0.8s linear infinite; }
  @keyframes pispin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`}</style>
    </div>
    </FillPage>
  );
};

export default CampaignsSummary;
